import { Grid, List } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import useNavigationSource from "../../../hooks/useNavigationSource";
import { canExpertEditCustomer } from "../../user/accessRightChecks";
import CardsList from "../CardsList";
import CheckboxFilter from "../CheckboxFilter";
import { selectCustomerOrganizationById } from "../customerOrganizationsSlice";
import { fetchCustomerUsersByCompanyId } from "../customerUsersSlice";
import {
  fetchWorkLocationsAndContractsByOrgId,
  selectAllCustomerContracts,
  selectAllCustomerWorkLocations,
} from "../customerWorkLocationsSlice";
import { ModalState } from "../types/types";
import { sortListItemsByActiveStatus } from "../utils";
import WhiteCard from "../WhiteCard";
import WorkLocationsForm from "./WorkLocationsForm";

const WorkLocations = () => {
  const [modalState, setModalState] = useState<ModalState>({
    show: false,
    id: "",
  });
  const [showRemoved, setShowRemoved] = useState(false);
  const [searchParams] = useSearchParams();

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isDirectNavigation } = useNavigationSource();

  const customerOrgId = searchParams.get("org");
  const { customerCompanyId } = useParams();

  const status = useAppSelector((state) => state.customerWorkLocations.status);
  const isLoading = status === "pending";

  useEffect(() => {
    if (isDirectNavigation && customerCompanyId) {
      dispatch(fetchCustomerUsersByCompanyId({ customerCompanyId }));
    }
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirectNavigation]);

  const selectedOrganization = useAppSelector((state) =>
    selectCustomerOrganizationById(state, customerOrgId as string),
  );

  const workLocations = useAppSelector((state) =>
    selectAllCustomerWorkLocations(state),
  );

  const contracts = useAppSelector((state) =>
    selectAllCustomerContracts(state),
  );

  const workLocationsList = workLocations.map((workLocation) => {
    const contractByWorkLocationContractId = contracts.find(
      (contract) => contract.id === workLocation?.contractId,
    );

    return {
      name: workLocation.name,
      active: workLocation.active,
      contract: contractByWorkLocationContractId?.name,
      id: workLocation.id,
    };
  });

  const workLocationsByActiveStatus = showRemoved
    ? workLocationsList.sort(sortListItemsByActiveStatus)
    : workLocationsList
        .filter((workLocation) => workLocation.active === !showRemoved)
        .sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    if (selectedOrganization) {
      dispatch(
        fetchWorkLocationsAndContractsByOrgId({ id: selectedOrganization.id }),
      );
    }
  }, [dispatch, selectedOrganization]);

  const onOpen = (workLocationId?: string) =>
    setModalState({ show: true, id: workLocationId });

  const onClose = () => {
    setModalState({ show: false, id: "" });
  };

  return (
    <Grid container flexDirection="column">
      <OverlaySpinner isLoading={isLoading} />
      <Grid item container alignItems="center" padding="19px">
        <CheckboxFilter
          show={showRemoved}
          label={t("customers:workLocationNS:showRemovedWorkLocations")}
          onFilterChange={(show) => setShowRemoved(show)}
        />
      </Grid>
      <CardsList
        buttonLabel={t("common:workLocation")}
        onClick={(id?: string) => onOpen(id)}
        accessRightChecks={[canExpertEditCustomer]}
      >
        <List disablePadding>
          {workLocationsByActiveStatus.map((workLocation) => (
            <WhiteCard
              key={workLocation.id}
              onClick={() => onOpen(workLocation.id)}
              primary={workLocation.name}
              secondary={workLocation.contract}
              active={workLocation.active}
            />
          ))}
        </List>
      </CardsList>
      <DialogComponent
        variant="card"
        open={modalState.show}
        sx={{ minWidth: "1007px" }}
      >
        <WorkLocationsForm workLocationId={modalState.id} onClose={onClose} />
      </DialogComponent>
    </Grid>
  );
};

export default WorkLocations;
